/**
 * Sign-in backends of the web app (docs/adr/ADR-004):
 * - Identity Platform: Microsoft 365 (K-Rev tenant) and, for people outside
 *   K-Rev, e-mail and password with a verified address;
 * - development: an e-mail typed by the developer, token "dev:<e-mail>".
 */
import { type AuthConfigResponse, DEVELOPMENT_TOKEN_PREFIX } from '@k-management/shared';

export interface SignedInIdentity {
  email: string;
}

export interface SignInSession {
  config: AuthConfigResponse;
  /** Calls the listener now and at every sign-in or sign-out; returns the unsubscribe function. */
  subscribe(listener: (identity: SignedInIdentity | null) => void): () => void;
  /** Opens the Microsoft account picker. */
  signInWithMicrosoft(): Promise<void>;
  /** E-mail and password; in development mode the password is ignored. */
  signInWithPassword(email: string, password: string): Promise<void>;
  /** First access of an external user: creates the account and sends the verification e-mail. */
  createPasswordAccount(email: string, password: string): Promise<void>;
  sendPasswordReset(email: string): Promise<void>;
  resendVerification(): Promise<void>;
  /** Reloads the account after the user clicked the verification link; the caller checks again. */
  refresh(): Promise<void>;
  signOut(): Promise<void>;
  /** Token for the API, refreshed when it is about to expire; null when signed out. */
  getToken(): Promise<string | null>;
}

export async function createSession(config: AuthConfigResponse): Promise<SignInSession> {
  return config.mode === 'firebase'
    ? createFirebaseSession(config)
    : createDevelopmentSession(config);
}

/** Italian message for a failed sign-in, or null when the user just closed the window. */
export function signInErrorMessage(error: unknown): string | null {
  const code = (error as { code?: string } | null)?.code ?? '';
  switch (code) {
    case 'auth/popup-closed-by-user':
    case 'auth/cancelled-popup-request':
      return null;
    case 'auth/popup-blocked':
      return 'Il browser ha bloccato la finestra di accesso: consenti i popup per questo sito e riprova.';
    case 'auth/network-request-failed':
      return 'Connessione assente: riprova.';
    case 'auth/invalid-credential':
    case 'auth/wrong-password':
    case 'auth/user-not-found':
    case 'auth/invalid-email':
      return 'E-mail o password non corretti.';
    case 'auth/too-many-requests':
      return 'Troppi tentativi: attendi qualche minuto e riprova, oppure reimposta la password.';
    case 'auth/email-already-in-use':
      return 'Esiste già un account con questa e-mail: accedi, oppure usa «Password dimenticata».';
    case 'auth/weak-password':
    case 'auth/password-does-not-meet-requirements':
      return 'Password troppo debole: usa almeno 12 caratteri.';
    case 'auth/account-exists-with-different-credential':
      return 'Questa e-mail accede con un altro metodo: prova con Microsoft 365.';
    default:
      return 'Accesso non riuscito. Riprova o contatta l’amministratore.';
  }
}

async function createFirebaseSession(
  config: Extract<AuthConfigResponse, { mode: 'firebase' }>,
): Promise<SignInSession> {
  // Loaded only when Identity Platform is on: development builds do without it.
  const { initializeApp } = await import('firebase/app');
  const firebaseAuth = await import('firebase/auth');

  const auth = firebaseAuth.getAuth(initializeApp(config.firebase));
  auth.languageCode = 'it';
  const microsoft = new firebaseAuth.OAuthProvider('microsoft.com');
  // The K-Apps registration is single tenant: Microsoft must use the K-Rev directory.
  microsoft.setCustomParameters({ tenant: config.microsoftTenantId, prompt: 'select_account' });

  const continueUrl = { url: window.location.origin };

  return {
    config,
    subscribe: (listener) =>
      firebaseAuth.onAuthStateChanged(auth, (user) => {
        const email = user?.email ?? user?.providerData.find((p) => p.email)?.email;
        listener(user && email ? { email: email.toLowerCase() } : null);
      }),
    signInWithMicrosoft: async () => {
      await firebaseAuth.signInWithPopup(auth, microsoft);
    },
    signInWithPassword: async (email, password) => {
      await firebaseAuth.signInWithEmailAndPassword(auth, email.trim(), password);
    },
    createPasswordAccount: async (email, password) => {
      const credential = await firebaseAuth.createUserWithEmailAndPassword(
        auth,
        email.trim(),
        password,
      );
      await firebaseAuth.sendEmailVerification(credential.user, continueUrl);
    },
    sendPasswordReset: (email) =>
      firebaseAuth.sendPasswordResetEmail(auth, email.trim(), continueUrl),
    resendVerification: async () => {
      if (auth.currentUser) await firebaseAuth.sendEmailVerification(auth.currentUser, continueUrl);
    },
    refresh: async () => {
      // A new token carries the updated email_verified claim.
      await auth.currentUser?.reload();
      await auth.currentUser?.getIdToken(true);
    },
    signOut: () => firebaseAuth.signOut(auth),
    getToken: async () => (auth.currentUser ? auth.currentUser.getIdToken() : null),
  };
}

const DEVELOPMENT_EMAIL_KEY = 'k-management.development-email';

function createDevelopmentSession(config: AuthConfigResponse): SignInSession {
  const listeners = new Set<(identity: SignedInIdentity | null) => void>();
  const current = (): SignedInIdentity | null => {
    const email = localStorage.getItem(DEVELOPMENT_EMAIL_KEY);
    return email ? { email } : null;
  };
  const notify = () => listeners.forEach((listener) => listener(current()));
  const unsupported = async () => {
    throw new Error('Non disponibile in modalità sviluppo');
  };

  return {
    config,
    subscribe: (listener) => {
      listeners.add(listener);
      listener(current());
      return () => listeners.delete(listener);
    },
    signInWithMicrosoft: unsupported,
    signInWithPassword: async (email) => {
      localStorage.setItem(DEVELOPMENT_EMAIL_KEY, email.trim().toLowerCase());
      notify();
    },
    createPasswordAccount: unsupported,
    sendPasswordReset: unsupported,
    resendVerification: unsupported,
    refresh: async () => {},
    signOut: async () => {
      localStorage.removeItem(DEVELOPMENT_EMAIL_KEY);
      notify();
    },
    getToken: async () => {
      const identity = current();
      return identity ? `${DEVELOPMENT_TOKEN_PREFIX}${identity.email}` : null;
    },
  };
}
